import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Eye, Trash2, X, CheckCircle, Clock, FileText, XCircle } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from '../../config/axios';

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get('/admin/quotes');
      setQuotes(response.data.data.data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quote request?')) {
      try {
        await axios.delete(`/admin/quotes/${id}`);
        alert('Quote request deleted successfully!');
        fetchQuotes();
      } catch (error) {
        console.error('Error deleting quote:', error);
        alert('Failed to delete quote request');
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`/admin/quotes/${id}/status`, { status: newStatus });
      alert('Status updated successfully!');
      fetchQuotes();
      if (selectedQuote && selectedQuote.id === id) {
        setSelectedQuote({ ...selectedQuote, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axios.put(`/admin/quotes/${id}/read`);
      fetchQuotes();
      if (selectedQuote && selectedQuote.id === id) {
        setSelectedQuote({ ...selectedQuote, is_read: true });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const openViewModal = (quote) => {
    setSelectedQuote(quote);
    setShowModal(true);
    if (!quote.is_read) {
      handleMarkAsRead(quote.id);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedQuote(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
      reviewed: { color: 'bg-blue-100 text-blue-700', icon: Eye, label: 'Reviewed' },
      quoted: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Quoted' },
      closed: { color: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Closed' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center ${config.color}`}>
        <Icon size={14} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.service.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <AdminLayout>
      <Helmet>
        <title>Quote Requests - Admin Portal | BURALE</title>
      </Helmet>

      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-burale-dark mb-2">Quote Requests</h1>
            <p className="text-gray-600">Manage quotation requests from potential clients</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, company, or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-burale-teal focus:border-transparent outline-none"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="quoted">Quoted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-burale-teal"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuotes.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      No quote requests found
                    </td>
                  </tr>
                ) : (
                  filteredQuotes.map((quote) => (
                    <tr 
                      key={quote.id} 
                      className={`hover:bg-gray-50 ${!quote.is_read ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(quote.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {!quote.is_read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{quote.name}</div>
                            <div className="text-sm text-gray-500">{quote.email}</div>
                            {quote.company && (
                              <div className="text-xs text-gray-400">{quote.company}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                          {quote.service}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{quote.project_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{quote.location}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {quote.budget_range || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(quote.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => openViewModal(quote)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button 
                            onClick={() => handleDelete(quote.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showModal && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-burale-dark">Quote Request Details</h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Management */}
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Update Status</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStatusChange(selectedQuote.id, 'pending')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedQuote.status === 'pending'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedQuote.id, 'reviewed')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedQuote.status === 'reviewed'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Reviewed
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedQuote.id, 'quoted')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedQuote.status === 'quoted'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Quoted
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedQuote.id, 'closed')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedQuote.status === 'closed'
                        ? 'bg-gray-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Closed
                  </button>
                </div>
              </div>

              {/* Client Information */}
              <div>
                <h3 className="text-lg font-semibold text-burale-dark mb-3">Client Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Name</label>
                    <p className="text-burale-dark">{selectedQuote.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Company</label>
                    <p className="text-burale-dark">{selectedQuote.company || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Email</label>
                    <p className="text-burale-dark">{selectedQuote.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Phone</label>
                    <p className="text-burale-dark">{selectedQuote.phone}</p>
                  </div>
                </div>
              </div>

              {/* Project Information */}
              <div>
                <h3 className="text-lg font-semibold text-burale-dark mb-3">Project Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Service Required</label>
                    <p className="text-burale-dark">{selectedQuote.service}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Project Type</label>
                    <p className="text-burale-dark">{selectedQuote.project_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Location</label>
                    <p className="text-burale-dark">{selectedQuote.location}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Budget Range</label>
                    <p className="text-burale-dark">{selectedQuote.budget_range || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Timeline</label>
                    <p className="text-burale-dark">{selectedQuote.timeline || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Submitted</label>
                    <p className="text-burale-dark">
                      {new Date(selectedQuote.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div>
                <h3 className="text-lg font-semibold text-burale-dark mb-3">Project Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedQuote.message}</p>
                </div>
              </div>

              {/* Contact Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <a
                  href={`mailto:${selectedQuote.email}`}
                  className="flex-1 bg-burale-teal text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all text-center"
                >
                  Send Email
                </a>
                <a
                  href={`tel:${selectedQuote.phone}`}
                  className="flex-1 bg-burale-dark text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all text-center"
                >
                  Call Client
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Quotes;
